import { newsList } from "@/mocks/news";
import type { NewsItem } from "@/types/content";

const allNews = newsList as NewsItem[];

export function getNewsById(id: string): NewsItem | undefined {
  return allNews.find((item) => item.id === id);
}

export function getByCategory(key: string): NewsItem[] {
  return allNews.filter((item) => item.categoryKey === key);
}

export function getFeatured(): NewsItem[] {
  return allNews.filter((item) => item.featured);
}

export function getLatest(count = 6): NewsItem[] {
  return [...allNews]
    .sort((a, b) => +new Date(b.publishDate) - +new Date(a.publishDate))
    .slice(0, count);
}

export function getPopular(count = 6): NewsItem[] {
  return [...allNews].sort((a, b) => b.views - a.views).slice(0, count);
}

export function getRelated(item: NewsItem, count = 3): NewsItem[] {
  const sameCategory = allNews.filter(
    (n) => n.categoryKey === item.categoryKey && n.id !== item.id
  );
  if (sameCategory.length >= count) {
    return sameCategory.sort((a, b) => b.views - a.views).slice(0, count);
  }
  const others = allNews.filter(
    (n) => n.id !== item.id && n.categoryKey !== item.categoryKey
  );
  return [...sameCategory, ...others].slice(0, count);
}

export type SortKey = "latest" | "hot" | "recommend";

export function filterAndSort(
  list: NewsItem[],
  options: {
    sort?: SortKey;
    subType?: string;
    timeRange?: string;
    hotOnly?: boolean;
  }
): NewsItem[] {
  let result = [...list];

  if (options.subType && options.subType !== "全部") {
    result = result.filter((item) => item.subType === options.subType);
  }

  if (options.hotOnly) {
    result = result.filter((item) => item.views >= 30000);
  }

  if (options.timeRange && options.timeRange !== "全部") {
    const now = new Date("2026-10-15T00:00:00");
    const days =
      options.timeRange === "一周内" ? 7 : options.timeRange === "一个月内" ? 30 : 90;
    result = result.filter((item) => {
      const diff = (now.getTime() - new Date(item.publishDate).getTime()) / 86400000;
      return diff <= days;
    });
  }

  const sort = options.sort ?? "latest";
  if (sort === "latest") {
    result.sort((a, b) => +new Date(b.publishDate) - +new Date(a.publishDate));
  } else if (sort === "hot") {
    result.sort((a, b) => b.views - a.views);
  } else {
    result.sort(
      (a, b) => Number(b.featured) - Number(a.featured) || b.views - a.views
    );
  }

  return result;
}

export function searchNews(keyword: string, categoryKeys: string[]): NewsItem[] {
  const kw = keyword.trim().toLowerCase();
  let result = [...allNews];
  if (categoryKeys.length > 0) {
    result = result.filter((item) => categoryKeys.includes(item.categoryKey));
  }
  if (kw) {
    result = result.filter(
      (item) =>
        item.title.toLowerCase().includes(kw) ||
        item.summary.toLowerCase().includes(kw) ||
        item.subType.toLowerCase().includes(kw) ||
        item.tags.some((t) => t.toLowerCase().includes(kw))
    );
  }
  return result.sort((a, b) => b.views - a.views);
}

export function formatDate(value: string): string {
  const d = new Date(value);
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${y}.${m}.${day}`;
}

export function formatViews(value: number): string {
  if (value >= 10000) {
    return `${(value / 10000).toFixed(1)} 万`;
  }
  return `${value}`;
}